import { useEffect, useState, useCallback, useRef } from "react";
import api, { mediaUrl, formatError, uploadFileChunked } from "../lib/api";
import { toast } from "sonner";
import { MultiProductPicker } from "../components/MultiProductPicker";
import { shareMediaFile } from "../lib/share";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { PushDialog } from "./AiSlides";
import {
  Clapperboard, Loader2, Film, Plus, Trash2, Send, Download, ImageUp, Wand2, AlertTriangle, Share2, Sparkles, Upload, GitCompareArrows,
} from "lucide-react";

export const ENGINE_LABELS = {
  "kling-3-pro": "Kling 3 Pro",
  "kling-3-turbo": "Kling 3 Turbo",
  "kling-2.5": "Kling 2.5",
  "veo3.1": "Veo 3.1",
  "veo3-fast": "Veo 3 Fast",
  "seedance-pro": "Seedance Pro",
  "runway-gen4.5": "Runway Gen-4.5",
};
const COMPARE_ENGINES = ["kling-3-pro", "veo3.1"];

const MOTION_STYLES = [
  { key: "dramatic-reveal", label: "Dramatic Reveal", desc: "Fast dolly-in, hero reveal, lighting snap" },
  { key: "orbit-spin", label: "Orbit / Spin", desc: "Camera circles the subject with energy" },
  { key: "slow-mo-splash", label: "Slow-Mo Splash", desc: "Pour, splash & droplets in slow motion" },
  { key: "punchy-zoom", label: "Punchy Zoom", desc: "Snap zooms, bold social-media pace" },
  { key: "floating-hero", label: "Floating Hero", desc: "Product floats & rotates, premium feel" },
  { key: "cinematic-push", label: "Cinematic Push", desc: "Calm, subtle, smooth push-in" },
];

function GenerateClipDialog({ onClose, onSaved }) {
  const [mode, setMode] = useState("text");
  const [prompt, setPrompt] = useState("");
  const [scene, setScene] = useState("");
  const [direction, setDirection] = useState("");
  const [engine, setEngine] = useState("kling-3-pro");
  const [motionStyle, setMotionStyle] = useState("dramatic-reveal");
  const [compare, setCompare] = useState(false);
  const [seconds, setSeconds] = useState("8");
  const [orientation, setOrientation] = useState("landscape");
  const [busy, setBusy] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [products, setProducts] = useState([]);

  const polishPrompt = async () => {
    if (!prompt.trim()) return toast.error("Type a short idea first");
    setPolishing(true);
    try {
      const { data } = await api.post("/media/polish-clip-prompt", { prompt: prompt.trim(), orientation });
      if (data?.prompt) { setPrompt(data.prompt); toast.success("Prompt polished — tweak it or generate"); }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Couldn't polish the prompt");
    }
    setPolishing(false);
  };

  const generate = async () => {
    if (mode === "product" && products.length === 0) return toast.error("Upload at least one product photo");
    if (mode === "text" && !prompt.trim()) return toast.error("Describe the clip");
    setBusy(true);
    const buildFd = (eng, group) => {
      const fd = new FormData();
      fd.append("prompt", prompt.trim());
      fd.append("scene", scene.trim());
      fd.append("direction", direction.trim());
      fd.append("engine", eng);
      fd.append("seconds", seconds);
      fd.append("orientation", orientation);
      fd.append("motion_style", motionStyle);
      if (group) fd.append("compare_group", group);
      if (mode === "product") products.forEach((p) => { fd.append("files", p.file); fd.append("descriptions", p.desc || ""); });
      return fd;
    };
    try {
      if (compare) {
        const group = (window.crypto?.randomUUID?.() || `cmp-${Date.now()}`);
        await Promise.all(COMPARE_ENGINES.map((eng) =>
          api.post("/media/generate-clip", buildFd(eng, group), { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 })
        ));
        toast.success("⚔️ Compare started — Kling 3 Pro vs Veo 3.1 render side-by-side below (usually 1–3 min)");
      } else {
        const res = await api.post("/media/generate-clip", buildFd(engine, ""), { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
        if (res.data?.fell_back_from) {
          toast.info("That engine wasn't available — rendering this one with Kling instead. It appears below when ready.");
        } else {
          toast.success("🎬 Clip is rendering — it appears below when ready (usually 1–3 min)");
        }
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not start the clip");
    }
    setBusy(false);
  };

  return (
    <DialogContent className="max-w-2xl w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-lg md:text-xl pr-6 flex items-center gap-2"><Clapperboard className="w-5 h-5 text-primary" /> Generate a video clip</DialogTitle>
        <DialogDescription className="sr-only">Create a short AI video clip with Sora, from a description or a product photo.</DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-secondary/50 border border-border">
        <button type="button" onClick={() => setMode("text")} data-testid="clip-mode-text"
          className={`flex items-center justify-center gap-2 rounded-md py-2 text-sm font-medium transition-colors ${mode === "text" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          <Wand2 className="w-4 h-4" /> Describe it
        </button>
        <button type="button" onClick={() => setMode("product")} data-testid="clip-mode-product"
          className={`flex items-center justify-center gap-2 rounded-md py-2 text-sm font-medium transition-colors ${mode === "product" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          <ImageUp className="w-4 h-4" /> Animate my photo
        </button>
      </div>

      <div className="space-y-3">
        {mode === "product" && (
          <div>
            <MultiProductPicker items={products} setItems={setProducts} max={4} />
            <p className="text-[11px] text-muted-foreground mt-1">Animate <b>any</b> photo — a person, a scene, or a product. Upload 1 image to bring it to life (or up to <b>4</b> to feature together). Leave the description blank for a natural "living photo".</p>
          </div>
        )}
        <div>
          <div className="flex items-center justify-between gap-2">
            <Label>{mode === "product" ? "Motion / vibe (optional)" : "What should the clip show?"}</Label>
            {mode === "text" && (
              <button type="button" onClick={polishPrompt} disabled={polishing || !prompt.trim()}
                className="text-xs flex items-center gap-1 text-primary hover:underline disabled:opacity-40 disabled:no-underline" data-testid="polish-prompt-btn">
                {polishing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Polish prompt
              </button>
            )}
          </div>
          {mode === "text" ? (
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} className="mt-1 resize-none" data-testid="clip-prompt-input"
              placeholder="fresh produce on a market table, gentle camera push-in" />
          ) : (
            <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} className="mt-1" data-testid="clip-prompt-input"
              placeholder="optional — e.g. subtle motion, gentle camera push-in" />
          )}
          {mode === "text" && <p className="text-[11px] text-muted-foreground mt-1">Tip: tap <b>Polish prompt</b> to turn a short idea into a rich cinematic prompt — big quality boost.</p>}
          {mode === "product" && <p className="text-[11px] text-muted-foreground mt-1">Leave blank for natural "living photo" motion, or describe the movement you want.</p>}
        </div>
        <div>
          <Label>Setting (optional)</Label>
          <Input value={scene} onChange={(e) => setScene(e.target.value)} className="mt-1" placeholder="bright modern grocery aisle" data-testid="clip-scene-input" />
        </div>
        <div>
          <Label className="flex items-center gap-1.5">🎬 Direction &amp; mood <span className="text-[10px] font-normal text-muted-foreground">(shapes the look — never shown as text)</span></Label>
          <Textarea value={direction} onChange={(e) => setDirection(e.target.value)} rows={3} className="mt-1 resize-none" data-testid="clip-direction-input"
            placeholder="Describe the vibe you want, e.g. “steam rising off a sizzling dish fresh out of the oven, dramatic close-up, glistening cheese pull, warm cinematic lighting — make people drool.”" />
        </div>
        <div>
          <Label className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-primary" /> Motion style</Label>
          <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">Sets how the camera and subject move — this is what makes a clip feel alive instead of flat.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="clip-motion-styles">
            {MOTION_STYLES.map((m) => (
              <button type="button" key={m.key} onClick={() => setMotionStyle(m.key)} data-testid={`motion-style-${m.key}`}
                className={`text-left rounded-lg border px-2.5 py-2 transition-colors ${motionStyle === m.key ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}>
                <span className="block text-xs font-semibold">{m.label}</span>
                <span className="block text-[10px] text-muted-foreground leading-tight mt-0.5">{m.desc}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label>Engine</Label>
            <Select value={engine} onValueChange={setEngine} disabled={compare}>
              <SelectTrigger className="mt-1" data-testid="clip-engine-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="kling-3-pro">Kling 3 Pro (best quality — recommended)</SelectItem>
                <SelectItem value="veo3.1">Veo 3.1 (Google — premium)</SelectItem>
                <SelectItem value="kling-3-turbo">Kling 3 Turbo (faster, lower cost)</SelectItem>
                <SelectItem value="seedance-pro">Seedance Pro (cheapest)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Length</Label>
            <Select value={seconds} onValueChange={setSeconds}>
              <SelectTrigger className="mt-1" data-testid="clip-seconds-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="4">4 sec</SelectItem>
                <SelectItem value="8">8 sec</SelectItem>
                <SelectItem value="12">12 sec</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Shape</Label>
            <Select value={orientation} onValueChange={setOrientation}>
              <SelectTrigger className="mt-1" data-testid="clip-orientation-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="landscape">TV (16:9)</SelectItem>
                <SelectItem value="portrait">Phone (9:16)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <button type="button" onClick={() => setCompare((v) => !v)} data-testid="clip-compare-toggle"
          className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${compare ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>
          <span className={`flex h-5 w-5 items-center justify-center rounded border flex-shrink-0 ${compare ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"}`}>
            {compare && <GitCompareArrows className="w-3.5 h-3.5" />}
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-medium flex items-center gap-1.5"><GitCompareArrows className="w-4 h-4 text-primary" /> Compare mode</span>
            <span className="block text-[11px] text-muted-foreground">Render the same prompt on <b>Kling 3 Pro</b> and <b>Veo 3.1</b> at once, side-by-side, to pick the best motion. Uses ~2× credits.</span>
          </span>
        </button>
        <p className="text-[11px] text-amber-400/90 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Clips take ~30–90s to render and use noticeably more credits than images. Clips are silent (TVs stay muted).</p>
      </div>

      <DialogFooter className="mt-2">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={generate} disabled={busy} data-testid="clip-generate-btn">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</> : compare ? <><GitCompareArrows className="w-4 h-4 mr-2" /> Compare engines</> : <><Clapperboard className="w-4 h-4 mr-2" /> Generate clip</>}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function ClipCard({ m, onPush, onRemove, badge, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden fade-up" data-testid={testid || `ai-clip-${m.id}`}>
      <div className="aspect-video bg-black flex items-center justify-center relative">
        {badge && (
          <span className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full bg-black/70 backdrop-blur text-[11px] font-semibold text-white border border-white/20" data-testid={`clip-engine-badge-${m.id}`}>{badge}</span>
        )}
        {m.clip_status === "ready" ? (
          <video src={mediaUrl(m.id)} className="w-full h-full object-cover" muted loop autoPlay playsInline data-testid={`clip-video-${m.id}`} />
        ) : m.clip_status === "failed" ? (
          <div className="text-center text-destructive text-xs px-3" data-testid={`clip-failed-${m.id}`}><AlertTriangle className="w-6 h-6 mx-auto mb-1.5" />{m.clip_error || "Failed — try again"}</div>
        ) : (
          <div className="text-center text-muted-foreground text-xs px-3" data-testid={`clip-processing-${m.id}`}>
            <Loader2 className="w-6 h-6 mx-auto mb-1.5 animate-spin text-primary" />
            <div className="font-medium text-foreground">Rendering{typeof m.clip_progress === "number" && m.clip_progress > 0 ? ` ${m.clip_progress}%` : "…"}</div>
            {m.retry_count > 0 && (
              <div className="text-[10px] mt-1 text-amber-400" data-testid={`clip-retry-${m.id}`}>
                {m.retry_reason || "engine was busy — retrying"} (attempt {m.retry_count + 1} of {(m.retry_max || 2) + 1})
              </div>
            )}
            <div className="text-[10px] mt-1 opacity-70">usually ~1–3 min</div>
          </div>
        )}
      </div>
      <div className="p-3 space-y-2">
        <p className="font-semibold text-sm truncate">{m.original_filename}</p>
        <div className="flex items-center gap-2">
          <Button size="sm" className="flex-1 h-8 text-xs" disabled={m.clip_status !== "ready"} onClick={() => onPush(m)} data-testid={`push-clip-${m.id}`}>
            <Send className="w-3.5 h-3.5 mr-1.5" /> Push
          </Button>
          <a href={m.clip_status === "ready" ? mediaUrl(m.id) : undefined} download={m.original_filename}
             className={`h-8 px-2 inline-flex items-center rounded-md border border-border text-muted-foreground ${m.clip_status === "ready" ? "hover:text-primary" : "opacity-40 pointer-events-none"}`}
             data-testid={`download-clip-${m.id}`}><Download className="w-3.5 h-3.5" /></a>
          <Button size="sm" variant="secondary" className="h-8 px-2" disabled={m.clip_status !== "ready"} onClick={() => shareMediaFile(m)} data-testid={`share-clip-${m.id}`} title="Send as video to WhatsApp/Instagram">
            <Share2 className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="ghost" className="h-8 px-2 text-muted-foreground hover:text-destructive" onClick={() => onRemove(m)} data-testid={`delete-clip-${m.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AiClips() {
  const [media, setMedia] = useState([]);
  const [tvs, setTvs] = useState([]);
  const [genOpen, setGenOpen] = useState(false);
  const [pushClip, setPushClip] = useState(null);
  const [uploadingClip, setUploadingClip] = useState(false);
  const clipFileRef = useRef();

  const load = useCallback(() => api.get("/media").then((r) => {
    if (Array.isArray(r.data)) setMedia((prev) => (r.data.length === 0 && prev.length > 0 ? prev : r.data));
  }).catch(() => {}), []);
  useEffect(() => {
    load();
    api.get("/tvs").then((r) => setTvs(r.data)).catch(() => {});
  }, [load]);

  // Phones pause JS timers when the tab is backgrounded / screen locks — so refetch on wake
  // to pick up clips the server finished while we were asleep.
  useEffect(() => {
    const onWake = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    return () => {
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, [load]);

  const clips = media.filter((m) => m.is_clip);

  // Group compare-mode clips (same compare_group) so they render side-by-side.
  const compareGroups = [];
  const groupIndex = {};
  const soloClips = [];
  clips.forEach((c) => {
    if (c.compare_group) {
      if (groupIndex[c.compare_group] === undefined) {
        groupIndex[c.compare_group] = compareGroups.length;
        compareGroups.push({ group: c.compare_group, items: [] });
      }
      compareGroups[groupIndex[c.compare_group]].items.push(c);
    } else {
      soloClips.push(c);
    }
  });

  // poll processing clips; only re-subscribe when the SET of processing clips changes
  const processingIds = clips.filter((c) => c.clip_status === "processing").map((c) => c.id).join(",");
  useEffect(() => {
    if (!processingIds) return;
    const ids = processingIds.split(",");
    const t = setInterval(async () => {
      for (const id of ids) {
        try {
          const { data } = await api.get(`/media/clip/${id}`);
          setMedia((prev) => prev.map((m) => (m.id === data.id ? { ...m, ...data } : m)));
          if (data.clip_status === "failed") toast.error(`A clip failed: ${data.clip_error || "please try again"}`);
        } catch { /* keep trying */ }
      }
    }, 4000);
    return () => clearInterval(t);
  }, [processingIds]);

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.original_filename}"?`)) return;
    try { await api.delete(`/media/${m.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const onUploadClip = async (e) => {
    const f = e.target.files?.[0];
    if (clipFileRef.current) clipFileRef.current.value = "";
    if (!f) return;
    if (!(f.type || "").startsWith("video") && !/\.(mp4|mov|webm|m4v|avi|mkv)$/i.test(f.name)) {
      return toast.error("Please choose a video file (MP4/MOV).");
    }
    setUploadingClip(true);
    try {
      const doc = await uploadFileChunked(f);
      if (doc?.id) {
        await api.post(`/media/${doc.id}/mark-clip`);
        toast.success("Clip uploaded — ready to push to your TVs");
        load();
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Upload failed");
    }
    setUploadingClip(false);
  };

  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8 max-w-5xl">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-display font-bold tracking-tight flex items-center gap-2">
            <Clapperboard className="w-6 h-6 text-primary" /> AI Clips
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Generate short AI clips (Kling 2.5, Veo 3 Fast, Seedance) — or upload a video you made elsewhere (e.g. Dola) — then loop them on your TVs.</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <input ref={clipFileRef} type="file" accept="video/*" hidden onChange={onUploadClip} data-testid="upload-clip-input" />
          <Button variant="secondary" disabled={uploadingClip} onClick={() => clipFileRef.current?.click()} data-testid="upload-clip-btn">
            {uploadingClip ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />} Upload clip
          </Button>
          <Button onClick={() => setGenOpen(true)} data-testid="new-clip-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Clip
          </Button>
        </div>
      </div>

      <div className="mb-5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 flex items-start gap-2.5" data-testid="seedance-notice">
        <Sparkles className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-emerald-100/90 leading-relaxed">
          Upgraded engines: <b>Kling 3 Pro</b> (default) and <b>Veo 3.1</b> (Google) — the current top video
          models, billed to your existing Universal Key, no new account. A/B them to see which looks best.
          (OpenAI's Sora API was permanently shut down, so it's no longer offered.) For a free rolling promo, use <b>Playlists → Quick Loop</b>.
        </p>
      </div>

      {clips.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center" data-testid="ai-clips-empty">
          <Film className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-muted-foreground">No clips yet. Click <b>New Clip</b> to generate one, or <b>Upload clip</b> to add a video you made elsewhere (e.g. Dola).</p>
        </div>
      ) : (
        <div className="space-y-6">
          {compareGroups.length > 0 && (
            <div className="space-y-4" data-testid="ai-clips-compare-section">
              {compareGroups.map(({ group, items }) => (
                <div key={group} className="rounded-xl border border-primary/30 bg-primary/[0.04] p-3 md:p-4" data-testid={`compare-group-${group}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <GitCompareArrows className="w-4 h-4 text-primary" />
                    <h2 className="text-sm font-semibold">Compare — pick the best motion</h2>
                    <span className="text-[11px] text-muted-foreground">({items.length} engines)</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {items.map((m) => (
                      <ClipCard key={m.id} m={m} onPush={setPushClip} onRemove={remove}
                        badge={ENGINE_LABELS[m.engine] || m.engine || "Engine"} testid={`compare-clip-${m.id}`} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {soloClips.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ai-clips-list">
              {soloClips.map((m) => (
                <ClipCard key={m.id} m={m} onPush={setPushClip} onRemove={remove} />
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        {genOpen && <GenerateClipDialog onClose={() => setGenOpen(false)} onSaved={load} />}
      </Dialog>
      <Dialog open={!!pushClip} onOpenChange={(o) => !o && setPushClip(null)}>
        {pushClip && <PushDialog slide={pushClip} tvs={tvs} onClose={() => setPushClip(null)} onPushed={() => {}} />}
      </Dialog>
    </div>
  );
}
